import { useBulkFetchSchedules } from '@/features/planner/queries/useBulkFetchSchedules';
import { useBulkFetchTimelineItems } from '@/features/planner/queries/useBulkFetchTimelineItems';
import type { PlanSchedulingScenarioContext } from '../adapters/types';
import { useSchedulesQueryWithPublished } from './useSchedulesQueryWithPublished';

interface ScheduleBarData {
  forecastedStart: string | undefined;
  forecastedFinish: string | undefined;
  latestFinish: string | undefined;
  deadline: string | undefined;
  ancestorDeadline: string | undefined;
  ancestorDeadlineSourceItemName: string | undefined;
  isLoading: boolean;
}

export const useScheduleBarData = (
  itemId: number,
  scenarioContext?: PlanSchedulingScenarioContext,
  enabled = true,
): ScheduleBarData => {
  const { data: timelineItems, isLoading: isTimelineLoading } = useBulkFetchTimelineItems({
    loopId: scenarioContext?.loopId ?? 0,
    scenarioId: scenarioContext?.scenarioId ?? 0,
    itemIds: scenarioContext ? [itemId] : [],
    enabled,
  });

  const { data: scenarioSchedules, isLoading: isScenarioScheduleLoading } = useBulkFetchSchedules({
    loopId: scenarioContext?.loopId ?? 0,
    scenarioId: scenarioContext?.scenarioId ?? 0,
    itemIds: scenarioContext ? [itemId] : [],
    enabled,
  });

  const { data: schedulesData, isLoading: isScheduleLoading } = useSchedulesQueryWithPublished(
    itemId,
    enabled && !scenarioContext,
  );

  if (!enabled) {
    return {
      forecastedStart: undefined,
      forecastedFinish: undefined,
      latestFinish: undefined,
      deadline: undefined,
      ancestorDeadline: undefined,
      ancestorDeadlineSourceItemName: undefined,
      isLoading: false,
    };
  }

  if (scenarioContext) {
    const timelineItem = timelineItems?.find((t) => t.item.itemId === itemId);
    const schedule = scenarioSchedules?.[itemId];
    const riskInfo = timelineItem?.riskInfo;
    return {
      forecastedStart: schedule?.forecastedStartDateTime ?? undefined,
      forecastedFinish: schedule?.forecastedFinishDateTime ?? undefined,
      latestFinish: schedule?.latestFinishDateTime ?? undefined,
      deadline: timelineItem?.scenarioItem.deadlineDateTime ?? undefined,
      ancestorDeadline: riskInfo?.earliestAncestorDeadlineDateTime ?? undefined,
      ancestorDeadlineSourceItemName: riskInfo?.earliestAncestorDeadlineSourceItemName ?? undefined,
      isLoading: isTimelineLoading || isScenarioScheduleLoading,
    };
  }

  const schedule = schedulesData?.[itemId];
  return {
    forecastedStart: schedule?.forecastedStartDateTime ?? undefined,
    forecastedFinish: schedule?.forecastedFinishDateTime ?? undefined,
    latestFinish: schedule?.latestFinishDateTime ?? undefined,
    deadline: undefined,
    ancestorDeadline: undefined,
    ancestorDeadlineSourceItemName: undefined,
    isLoading: isScheduleLoading,
  };
};
